import { enablePointerEvents } from '@utils/pointer-events-control';
import { config, resetGameState } from '@js/state';

/**
 * StartScene — title art (start-scene-bg.png already contains the "Pizza
 * Party / Fraction game" branding and the landing decoration) with an
 * always-looping conveyor on the left (same x/size as GameScene) and a
 * Play CTA on top.
 */
const BELT_SCROLL_SPEED = 320;

export default class StartScene extends Phaser.Scene {
  constructor() {
    super({ key: 'StartScene' });
  }

  create() {
    // Enable pointer events so hover states and the cursor work
    enablePointerEvents();

    const { width: W, height: H } = config;

    this.cameras.main.setBackgroundColor('#FFE9CC');
    this.cameras.main.fadeIn(400, 0, 0, 0);

    if (this.textures.exists('start-scene-bg')) {
      const bg = this.add.image(W / 2, H / 2, 'start-scene-bg');
      const s = Math.max(W / bg.width, H / bg.height);
      bg.setScale(s);
    }

    // Looping conveyor — matches GameScene/TutorialScene placement.
    if (this.textures.exists('conveyor-belt-1')) {
      const tex = this.textures.get('conveyor-belt-1').getSourceImage();
      this.conveyor = this.add
        .tileSprite(320, H / 2, 360, H, 'conveyor-belt-1')
        .setOrigin(0.5);
      this.beltTileScale = 360 / tex.width;
      this.conveyor.setTileScale(this.beltTileScale, this.beltTileScale);
    }

    // Play button: Figma top-left (835, 450), size 200×56 → centre (935, 478).
    this.buildPlayCta(935, 478, () => this.startGame());

    resetGameState();
  }

  update(_t, dt) {
    if (this.conveyor) {
      const pxPerSec = BELT_SCROLL_SPEED / (this.beltTileScale || 1);
      this.conveyor.tilePositionY -= (dt / 1000) * pxPerSec;
    }
  }

  buildPlayCta(x, y, onClick) {
    // ─── Pre-flight ───
    // Inputs: pointerdown/up on the CTA sprite.
    // State mutated: disableInteractive() and onClick fires scene.start (one-shot).
    // Re-entry guards: disableInteractive on first pointerup before onClick.
    // Cleanup: scene shutdown destroys.
    // Manually verified: [x] double click [x] click during fade [x] rapid clicks
    const idleKey = 'cta-button';
    const hoverKey = 'play-button-hover';

    if (!this.textures.exists(idleKey)) {
      const txt = this.add.text(x, y, 'PLAY', {
        fontFamily: 'font2', fontSize: '36px', color: '#FFFFFF',
        backgroundColor: '#22A06B', padding: { x: 24, y: 12 },
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      txt.once('pointerup', () => { txt.disableInteractive(); onClick(); });
      return;
    }

    // The Figma export of `cta-button` / `play-button-hover` already has the
    // "PLAY" label baked into the PNG — do NOT overlay a Phaser text node.
    // Display size matches the Figma 200×56 spec.
    const targetW = 200;
    const targetH = 56;
    const btn = this.add.image(x, y, idleKey)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    btn.setDisplaySize(targetW, targetH);

    const hasHover = this.textures.exists(hoverKey);
    btn.on('pointerover', () => {
      if (hasHover) btn.setTexture(hoverKey);
      btn.setDisplaySize(targetW, targetH);
    });
    btn.on('pointerout', () => {
      btn.setTexture(idleKey);
      btn.setDisplaySize(targetW, targetH);
    });
    btn.on('pointerdown', () => btn.setDisplaySize(targetW * 0.96, targetH * 0.96));
    btn.on('pointerup', () => {
      btn.disableInteractive();
      btn.setDisplaySize(targetW, targetH);
      onClick();
    });
  }

  startGame() {
    this.cameras.main.fadeOut(400, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('TutorialScene');
    });
  }
}
